import React from 'react';
import { Components } from '@rta/rta-blueprint-library';
import type { Island, Item, Kitchen, PanelItem } from '@rta/rta-blueprint-library';

import { getIslandAddonState } from '../helpers/island-addons';

/**
 * Historic constant kept for the MFG note: the most common flange is 1.625" and the note's
 * "All landing zones are 1.625\"W" clause only prints when every zone on the sheet matches it.
 */
export const LANDING_ZONE_WIDTH = 1.625;

/**
 * Real LANDING ZONES sheets are drawn at a FIXED scale (~1:13 against the 100px-per-paper-inch
 * sheet) regardless of how many diagrams a sheet carries — a two-diagram sheet leaves the body
 * half empty rather than drawing larger. Sheets paginate at 3-5 diagrams instead of shrinking.
 */
export const SCALE_PX_PER_IN = 7.7;

// Drawing weights in inches of viewBox space so every diagram renders identically at the shared scale.
const LANE_IN = 3.15;
const PAD_IN = 1.1;
const FONT_IN = 1.35;
const STROKE_OUTER_IN = 0.13;
const STROKE_DIM_IN = 0.11;
/** Band below the diagram for the FLANGE SIZE label (and the red count when grouped). */
const CAPTION_BAND_IN = 3.4;
/** Headroom on the strips form for the SKU line(s) and the two-headed flange-width leader. */
const STRIPS_HEAD_IN = FONT_IN * 3.4;
/** Strips are drawn butted with a small ARBITRARY gap, never at the real cutout separation (D2). */
const STRIPS_GAP_IN = 2.25;

const STROKE = '#1f3864';
const DIM = '#000';
const COUNT_RED = '#c00000';

// A cutout edge sitting on (or within half an inch of) the panel edge has nowhere for a zone.
const EDGE_EPSILON = 0.51;

const fmt = (value: number): string => `${Math.round(value * 10000) / 10000}"`;

/**
 * Per-appliance flange where the size-vs-cutout derivation is wrong: slide-in fridges have a
 * cutout WIDER than the unit (derivation goes negative) and a few drawers reveal differently.
 * Values measured off finalized sheets (landing-zones FINDINGS-drive.md evidence table).
 */
const FLANGE_OVERRIDES: Record<string, { w?: number; h?: number }> = {
	'C124R-S': { w: 1.0, h: 1.0 },
	'C115R-S': { w: 1.0, h: 1.0 },
	'C3-SSD28': { w: 0.625, h: 0.625 },
	'C3-TC': { w: 1.875, h: 1.625 },
	'C3-CD2DC': { w: 1.9375, h: 1.9375 }
};

/**
 * The appliance's flange per axis: the Submission Playbook defines Overall Width as "width with
 * Flanges", so flange = (overall − cutout) / 2 — exact for grills and doors against the finalized
 * sheets (C3SL42 → 1.25", C3-SA1724 → 0.75", C3-DA2431 → 1.625").
 */
export const getFlange = (item: Item): { w: number; h: number } => {
	const override = FLANGE_OVERRIDES[item.sku ?? ''];
	const size = item.size;
	const cutout = item.cutout ?? item.size;
	const derive = (overall?: number, inner?: number): number =>
		Math.max(0, Math.round((((overall ?? 0) - (inner ?? 0)) / 2) * 10000) / 10000);
	return {
		w: override?.w ?? derive(size?.width, cutout?.width),
		h: override?.h ?? derive(size?.height, cutout?.height)
	};
};

export type LandingZoneForm = 'ring' | 'strips';

export type LandingZoneEntry = {
	panelItem: PanelItem;
	form: LandingZoneForm;
	bandTop: boolean;
	bandBottom: boolean;
	flangeW: number;
	flangeH: number;
	cutoutWidth: number;
	cutoutHeight: number;
	/** strips form: the band height — the part of the cutout below the countertop. */
	stripHeight: number;
};

/**
 * Appliances flagged for landing zones — the catalog's component.landingZone, overridable per
 * placed item. The diagram FORM is appliance-driven, matching the finalized sheets:
 * - top-notched appliances (front-and-top placement / bound to the countertop) draw as two bare
 *   side STRIPS, no cutout rectangle, no bottom band — even with panel left underneath;
 * - everything else draws the nested-rect RING, dropping the top/bottom band where the flange is
 *   zero on that axis or the cutout sits flush with a panel edge (full-height fridges lose the
 *   bottom, giving the inverted-U the fridge trim-kit sheets show).
 */
export const eligibleItems = (island: Island, countertopThickness: number): Array<LandingZoneEntry> =>
	island.panels.children.flatMap((panel) =>
		panel.panelItemGroups.children.flatMap((group) =>
			group.items.children.flatMap((panelItem): Array<LandingZoneEntry> => {
				const { item } = panelItem;
				if (!item.landingZone) return [];
				if (item.placement === Components.PLACEMENT_TOP_ONLY) return [];
				const categoryKey = item.category?.key ?? '';
				if (item.category?.isVent || item.category?.isSpacer) return [];
				if (/cutout|outlet|hole|passthrough|system|insulated/i.test(categoryKey)) return [];
				const cutout = item.cutout ?? item.size;
				const cutoutWidth = cutout?.width ?? 0;
				const cutoutHeight = cutout?.height ?? 0;
				if (!(cutoutWidth > 0 && cutoutHeight > 0)) return [];

				const { w: flangeW, h: flangeH } = getFlange(item);
				if (flangeW <= 0 && flangeH <= 0) return [];

				// panel-item y runs from the panel CENTER, negative = top (see add-panel-vents)
				const halfPanel = (panel.size?.height ?? 0) / 2;
				const gapTop = (panelItem.center?.y ?? 0) - cutoutHeight / 2 + halfPanel;
				const gapBottom = halfPanel - ((panelItem.center?.y ?? 0) + cutoutHeight / 2);

				const topNotched = item.placement === Components.PLACEMENT_FRONT_AND_TOP || panelItem.boundTop;
				if (topNotched) {
					const stripHeight = Math.max(cutoutHeight - countertopThickness, 1);
					return [{ panelItem, form: 'strips' as const, bandTop: false, bandBottom: false, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight }];
				}

				// A fridge/trim kit seated on the panel bottom gets bare side strips at the full cutout
				// height (Porterfield C124R-S 34.5", Tieva p25 C124TK pairs) — never a ring.
				const bottomSeated = panelItem.fullHeight || (halfPanel > 0 && gapBottom <= EDGE_EPSILON) || /TK$/i.test(item.sku ?? '');
				if (bottomSeated) {
					return [{ panelItem, form: 'strips' as const, bandTop: false, bandBottom: false, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight: cutoutHeight }];
				}

				const bandTop = flangeH > 0 && (halfPanel === 0 || gapTop > EDGE_EPSILON);
				const bandBottom = flangeH > 0 && (halfPanel === 0 || gapBottom > EDGE_EPSILON);
				return [{ panelItem, form: 'ring' as const, bandTop, bandBottom, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight: 0 }];
			})));

export type DiagramGroup = {
	key: string;
	/** every SKU sharing this geometry — Briley p8 stacks C3C30/C3FTG30 over one strip pair (D5) */
	skus: Array<string>;
	count: number;
	form: LandingZoneForm;
	bandTop: boolean;
	bandBottom: boolean;
	flangeW: number;
	flangeH: number;
	cutoutWidth: number;
	cutoutHeight: number;
	stripHeight: number;
};

/**
 * Identical appliances share one diagram carrying a count. Real sheets mostly repeat diagrams,
 * but the counted form has precedent (QTY-2 above / a red x4 below) and saves sheets, so grouping
 * stays — styled as the red count below, per the Scott Briley sheet.
 */
export const groupEntries = (entries: Array<LandingZoneEntry>): Array<DiagramGroup> => {
	const groups = new Map<string, DiagramGroup>();
	entries.forEach(({ panelItem, form, bandTop, bandBottom, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight }) => {
		const sku = panelItem.item.sku ?? '';
		const key = [form, bandTop, bandBottom, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight].join('|');
		const existing = groups.get(key);
		if (existing) {
			existing.count += 1;
			if (!existing.skus.includes(sku)) existing.skus.push(sku);
		} else {
			groups.set(key, { key, skus: [sku], count: 1, form, bandTop, bandBottom, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight });
		}
	});
	return [...groups.values()];
};

/** A diagram's full extent in inches, caption band included — the pagination unit. */
export const diagramBox = (group: DiagramGroup): { viewWidth: number; viewHeight: number } => {
	const isStrips = group.form === 'strips';
	const bodyWidth = isStrips
		? group.flangeW * 2 + STRIPS_GAP_IN
		: group.cutoutWidth + group.flangeW * 2;
	const bodyHeight = isStrips
		? group.stripHeight
		: group.cutoutHeight + group.flangeH * ((group.bandTop ? 1 : 0) + (group.bandBottom ? 1 : 0));
	const stripsHead = isStrips ? STRIPS_HEAD_IN + FONT_IN * 1.1 * (group.skus.length - 1) : 0;
	return {
		viewWidth: bodyWidth + LANE_IN + PAD_IN,
		viewHeight: bodyHeight + LANE_IN + PAD_IN + CAPTION_BAND_IN + stripsHead
	};
};

/**
 * A dimension label sitting in its line needs the line BROKEN behind it, not just haloed: a glyph
 * casing leaves the line visible in the gaps between characters. `onLine` knocks out a text-sized
 * white box first, the real drafting convention.
 */
const DimLabel = (props: { x: number; y: number; text: string; fontSize: number; anchor?: 'start' | 'middle' | 'end'; rotate?: boolean; onLine?: boolean }): React.ReactElement => {
	const { x, y, text, fontSize, anchor = 'middle', rotate = false, onLine = false } = props;
	const label = (
		<text x={x} y={y} textAnchor={onLine ? 'middle' : anchor} fontSize={fontSize} fill={DIM} dominantBaseline={onLine ? 'central' : undefined}>{text}</text>
	);
	if (!onLine) return rotate ? <g transform={`rotate(-90 ${x} ${y})`}>{label}</g> : label;
	const boxWidth = text.length * fontSize * 0.56 + fontSize * 0.3;
	const boxHeight = fontSize * 1.15;
	const knockout = (
		<>
			<rect x={x - boxWidth / 2} y={y - boxHeight / 2} width={boxWidth} height={boxHeight} fill="#fff" stroke="none" />
			{label}
		</>
	);
	return rotate ? <g transform={`rotate(-90 ${x} ${y})`}>{knockout}</g> : <g>{knockout}</g>;
};

const HDim = (props: { x1: number; x2: number; y: number; label: string; fontSize: number; at?: number; onLine?: boolean }): React.ReactElement => {
	const { x1, x2, y, label, fontSize, at = 0.5, onLine = false } = props;
	return (
		<g>
			<line x1={x1} y1={y} x2={x2} y2={y} stroke={DIM} strokeWidth={STROKE_DIM_IN} markerStart="url(#lz-arrow-l)" markerEnd="url(#lz-arrow-r)" />
			<DimLabel x={x1 + (x2 - x1) * at} y={onLine ? y : y - fontSize * 0.45} text={label} fontSize={fontSize} onLine={onLine} />
		</g>
	);
};

const VDim = (props: { x: number; y1: number; y2: number; label: string; fontSize: number; at?: number; upright?: boolean; onLine?: boolean }): React.ReactElement => {
	const { x, y1, y2, label, fontSize, at = 0.5, upright = false, onLine = false } = props;
	const labelY = y1 + (y2 - y1) * at;
	const labelX = onLine ? x : x - fontSize * 0.85;
	return (
		<g>
			<line x1={x} y1={y1} x2={x} y2={y2} stroke={DIM} strokeWidth={STROKE_DIM_IN} markerStart="url(#lz-arrow-u)" markerEnd="url(#lz-arrow-d)" />
			{upright
				? <DimLabel x={labelX} y={labelY} text={label} fontSize={fontSize} anchor={onLine ? 'middle' : 'end'} onLine={onLine} />
				: <DimLabel x={labelX} y={labelY} text={label} fontSize={fontSize} rotate={true} onLine={onLine} />}
		</g>
	);
};

export type LandingZoneDiagramProps = DiagramGroup;

/**
 * One landing-zone diagram at the sheet's fixed scale, in the finalized sheets' two forms:
 * the nested-rect RING (bands dropped per side where no flange lands), and the bare side STRIPS
 * for top-notched appliances (no cutout rectangle, SKU above). FLANGE SIZE prints below each
 * diagram — the newest sheets' convention — and a grouped diagram carries a red count under it.
 */
export const LandingZoneDiagram = (props: LandingZoneDiagramProps): React.ReactElement => {
	const { skus, count, form, bandTop, bandBottom, flangeW, flangeH, cutoutWidth, cutoutHeight, stripHeight } = props;
	const fontSize = FONT_IN;
	const innerFont = fontSize * 0.92;
	const { viewWidth, viewHeight } = diagramBox(props);
	const outerWidth = cutoutWidth + flangeW * 2;
	const ox = LANE_IN;
	const oy = LANE_IN + (form === 'strips' ? STRIPS_HEAD_IN : 0);

	const markers = (
		<defs>
			<marker id="lz-arrow-r" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="0"><path d="M0,0 L6,3 L0,6 z" /></marker>
			<marker id="lz-arrow-l" viewBox="0 0 6 6" refX="1" refY="3" markerWidth="5" markerHeight="5" orient="180"><path d="M0,0 L6,3 L0,6 z" /></marker>
			<marker id="lz-arrow-d" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="90"><path d="M0,0 L6,3 L0,6 z" /></marker>
			<marker id="lz-arrow-u" viewBox="0 0 6 6" refX="1" refY="3" markerWidth="5" markerHeight="5" orient="270"><path d="M0,0 L6,3 L0,6 z" /></marker>
			<marker id="lz-arrow-auto" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,0 L6,3 L0,6 z" /></marker>
		</defs>
	);

	const caption = (bodyBottom: number): React.ReactElement => (
		<>
			<text x={ox + outerWidth / 2} y={bodyBottom + fontSize * 1.5} textAnchor="middle" fontSize={fontSize * 0.85}>{`FLANGE SIZE: ${fmt(flangeW)}`}</text>
			{count > 1
				? <text x={ox + outerWidth / 2} y={bodyBottom + fontSize * 2.85} textAnchor="middle" fontSize={fontSize * 1.2} fontWeight={700} fill={COUNT_RED}>{`x${count}`}</text>
				: null}
		</>
	);

	if (form === 'strips') {
		const leftX = ox;
		const rightX = ox + flangeW + STRIPS_GAP_IN;
		const bodyWidth = flangeW * 2 + STRIPS_GAP_IN;
		const bodyBottom = oy + stripHeight;
		const cx = ox + bodyWidth / 2;
		const leaderY = oy - fontSize * 1.1;
		return (
			<svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} width={viewWidth * SCALE_PX_PER_IN} height={viewHeight * SCALE_PX_PER_IN}>
				{markers}
				{/* SKU line(s) above — Briley p8 stacks every SKU sharing the geometry over one pair */}
				{skus.map((sku, index) => (
					<text key={sku} x={cx} y={leaderY - fontSize * (1.35 + 1.1 * (skus.length - 1 - index))} textAnchor="middle" fontSize={fontSize * 0.85} fontWeight={600}>{sku}</text>
				))}

				{/* one flange-width label on a two-headed leader spanning both strips */}
				<text x={cx} y={leaderY} textAnchor="middle" fontSize={fontSize * 0.85}>{fmt(flangeW)}</text>
				<line x1={cx - fontSize} y1={leaderY + fontSize * 0.2} x2={leftX + flangeW / 2} y2={oy - 0.15} stroke={DIM} strokeWidth={STROKE_DIM_IN} markerEnd="url(#lz-arrow-auto)" />
				<line x1={cx + fontSize} y1={leaderY + fontSize * 0.2} x2={rightX + flangeW / 2} y2={oy - 0.15} stroke={DIM} strokeWidth={STROKE_DIM_IN} markerEnd="url(#lz-arrow-auto)" />

				{/* the two strips, butted with an arbitrary small gap — never the real cutout separation */}
				<rect x={leftX} y={oy} width={flangeW} height={stripHeight} fill="none" stroke={STROKE} strokeWidth={STROKE_OUTER_IN} />
				<rect x={rightX} y={oy} width={flangeW} height={stripHeight} fill="none" stroke={STROKE} strokeWidth={STROKE_OUTER_IN} />

				<VDim x={ox - fontSize * 0.8} y1={oy} y2={bodyBottom} label={fmt(stripHeight)} fontSize={fontSize} />
				{count > 1
					? <text x={cx} y={bodyBottom + fontSize * 2.2} textAnchor="middle" fontSize={fontSize * 1.2} fontWeight={700} fill={COUNT_RED}>{`x${count}`}</text>
					: null}
			</svg>
		);
	}

	const outerHeight = cutoutHeight + flangeH * ((bandTop ? 1 : 0) + (bandBottom ? 1 : 0));
	const innerX = ox + flangeW;
	const innerY = oy + (bandTop ? flangeH : 0);
	const cx = innerX + cutoutWidth / 2;
	const cy = innerY + cutoutHeight / 2;
	const upright = cutoutHeight < innerFont * (fmt(cutoutHeight).length * 0.75);
	const bodyBottom = oy + outerHeight;

	return (
		<svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} width={viewWidth * SCALE_PX_PER_IN} height={viewHeight * SCALE_PX_PER_IN}>
			{markers}
			<rect x={ox} y={oy} width={outerWidth} height={outerHeight} fill="none" stroke={STROKE} strokeWidth={STROKE_OUTER_IN} />
			<rect x={innerX} y={innerY} width={cutoutWidth} height={cutoutHeight} fill="#fff" stroke={STROKE} strokeWidth={STROKE_OUTER_IN * 0.8} />

			<HDim x1={ox} x2={ox + outerWidth} y={oy - fontSize * 0.8} label={fmt(outerWidth)} fontSize={fontSize} />
			<VDim x={ox - fontSize * 0.8} y1={oy} y2={oy + outerHeight} label={fmt(outerHeight)} fontSize={fontSize} />

			{/* inner crosshair, each label seated in a break in its own line, staggered off centre */}
			<HDim x1={innerX} x2={innerX + cutoutWidth} y={cy} label={fmt(cutoutWidth)} fontSize={innerFont} at={0.74} onLine={true} />
			<VDim x={cx} y1={innerY} y2={innerY + cutoutHeight} label={fmt(cutoutHeight)} fontSize={innerFont} at={0.28} upright={upright} onLine={true} />

			{skus.map((sku, index) => (
				<text key={sku} x={innerX + cutoutWidth - fontSize * 0.4} y={innerY + cutoutHeight - fontSize * (0.5 + 1.1 * (skus.length - 1 - index))} textAnchor="end" fontSize={fontSize * 0.85} fontWeight={600}>{sku}</text>
			))}
			{caption(bodyBottom)}
		</svg>
	);
};

const DEFAULT_COUNTERTOP_THICKNESS = 1.25;

/** Every diagram group across the kitchen's flagged islands, in island order — the one list both
 * the pages builder (for chunking) and each sheet (for its slice) derive from, so they agree. */
export const buildLandingZoneGroups = (kitchen: Kitchen): Array<DiagramGroup> => {
	const thickness = kitchen.material?.countertop?.thickness ?? DEFAULT_COUNTERTOP_THICKNESS;
	const entries = kitchen.design.islands.children
		.filter((island) => getIslandAddonState(kitchen, island, 'landingZone'))
		.flatMap((island) => eligibleItems(island, thickness));
	return groupEntries(entries);
};

// The sheet body in px (measured off the rendered page) and the fixed-scale packing budget.
const SHEET_BODY_W_PX = 900;
const SHEET_BODY_H_PX = 416;
const SHEET_GAP_PX = 14;
const MAX_DIAGRAMS_PER_SHEET = 5;

/** Chunk the group list into sheets: row-packed at the FIXED scale, capped at 5 diagrams —
 * real sheets paginate rather than shrink (8 of 18 BPs run to a second LANDING ZONES sheet). */
export const chunkLandingZoneGroups = (groups: Array<DiagramGroup>): Array<[number, number]> => {
	const slices: Array<[number, number]> = [];
	let start = 0;
	let rowWidth = 0;
	let rowHeight = 0;
	let usedHeight = 0;
	let countInSheet = 0;
	groups.forEach((group, index) => {
		const { viewWidth, viewHeight } = diagramBox(group);
		const width = viewWidth * SCALE_PX_PER_IN;
		const height = viewHeight * SCALE_PX_PER_IN;
		const startsNewRow = rowWidth > 0 && rowWidth + SHEET_GAP_PX + width > SHEET_BODY_W_PX;
		const nextUsed = startsNewRow ? usedHeight + rowHeight + SHEET_GAP_PX : usedHeight;
		const nextRowHeight = startsNewRow ? height : Math.max(rowHeight, height);
		const overflows = countInSheet > 0 && (countInSheet >= MAX_DIAGRAMS_PER_SHEET || nextUsed + nextRowHeight > SHEET_BODY_H_PX);
		if (overflows) {
			slices.push([start, index]);
			start = index;
			rowWidth = width;
			rowHeight = height;
			usedHeight = 0;
			countInSheet = 1;
			return;
		}
		rowWidth = startsNewRow ? width : (rowWidth === 0 ? width : rowWidth + SHEET_GAP_PX + width);
		rowHeight = nextRowHeight;
		usedHeight = nextUsed;
		countInSheet += 1;
	});
	if (start < groups.length) slices.push([start, groups.length]);
	return slices.length ? slices : [[0, 0]];
};
